import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Reel} from 'com/gongxigames/math/core/game/Reel';
import {ReelSet} from 'com/gongxigames/math/core/game/ReelSet';
import {Spin} from 'com/gongxigames/math/core/game/Spin';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {
    AbstractReelsView,
    ReelsBasicState,
    ReelsEvents,
    ReelsInitState, ReelsQuickStopState, ReelsSequentialStopState, ReelsSpinningState,
    ReelsView, ReelsViewEvents
} from '@gongxigames/framework/slot/modules/reels';
import {GlobalEventsMap, GlobalEventsMapForBackwardCompatibility, ReasonsForDisablingSpin} from '@gongxigames/framework/slot/events';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {FirstReelStopTime, ReelBounceTime, ReelConstants} from '@gongxigames/framework/slot/utils';
import {Easing} from '@gongxigames/framework/core';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin';
import {AutoplayEvents} from '@gongxigames/framework/slot/modules/autoplay';
import {FreeSpinsModule} from '@gongxigames/framework/slot/modules/freespins';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus';
import {SpinButtonEvents} from '@gongxigames/framework/slot/modules/spinbutton';
import {AnticipatorEvents} from '@gongxigames/framework/slot/modules/anticipator';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsEvents';

export class ReelsModule extends Module<AbstractReelsView> {
    public static readonly NAME: symbol = Symbol('ReelsModule');

    public static readonly BASIC_STATE: string = 'basic';
    public static readonly INIT_STATE: string = 'init';
    public static readonly SPINNING_STATE: string = 'spinning';
    public static readonly STOPPING_STATE: string = 'stopping';
    public static readonly QUICKSTOP_STATE: string = 'quickstop';

    public static readonly RESPIN_BASIC_STATE: string = 'respin_basic';
    public static readonly RESPIN_SPINNING_STATE: string = 'respin_spinning';
    public static readonly RESPIN_QUICKSTOP_STATE: string = 'respin_quickstop';
    public static readonly RESPIN_STOPPING_STATE: string = 'respin_stopping';

    protected _reels!: Reel[];

    protected _isReelsCanBeStopped = false;
    protected _isAllAnimationsFinished = false;
    protected _isAllReelsStopped = false;
    protected _isTurboSpin = false;
    protected _anticipatedReels: number[] = [];

    protected _isBlockingFlow = false;

    protected _scatterHitAnimationsCounter = 0;

    protected _canSpinBySpinButton = true;
    protected _spinDisabledBy: ReasonsForDisablingSpin[] = [];

    protected _isRestoreAfterFS = false;

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle(BigWinEvents.STARTED);
        this.onCurrentStateHandle(BigWinEvents.FINISHED);
        this.onCurrentStateHandle(AutoplayEvents.AUTOPLAY_START_SPIN);
        this.onCurrentStateHandle('General.BuyFeature.BUY');
        this.onCurrentStateHandle(FreeSpinsModule.FREESPIN_START_SPIN);
        this.onCurrentStateHandle(WinPresentationEvents.PAY_ANIMATION_STARTED);
        this.onCurrentStateHandle(BuyBonusPopUpEvents.BUY_BUTTON_CLICKED);

        this.eventManager.on(SpinButtonEvents.SPIN_BUTTON_PRESSED, this.onSpinButtonPressed.bind(this), this, 999);

        this.onCurrentStateHandle(AnticipatorEvents.ANIMATION_STARTED);

        this.onCurrentStateHandle(FlowModuleEvents.RESPIN_WIN);

        this.onCurrentStateHandle(FlowModuleEvents.RESPIN_RESTORED);

        this.on(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, this.onInitialAnimationFinished);

        this.on(KeypadControlsEvents.TOTAL_BET_CHANGED, this.onBetChanged);

        this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponseReceived);
        this.on(LogicEvents.FREE_SPIN_RESPONSE, this.onSpinResponseReceived);
        this.on(LogicEvents.RESPIN_RESPONSE, this.onSpinResponseReceived);
        this.on(AnticipatorEvents.ANTICIPATION_REELS_RESULT_RECEIVED, this.onAnticipationResultReceived);

        this.on(WinPresentationEvents.PAUSED, this.onWinPresentationPaused);

        this.onCurrentStateHandle(WinPresentationEvents.INITIAL_ANIMATION_STARTED);

        this.onViewCurrentStateHandle(ReelsViewEvents.REEL_STOPPED);
        this.onView(ReelsViewEvents.ALL_REELS_STOPPED, this.onAllReelsStoppedCallback.bind(this));

        this.onView(ReelsViewEvents.ALL_REEL_ANIMATIONS_FINISHED, this.onAllReelAnimationsFinishedCallback.bind(this));

        this.onView(ReelsViewEvents.REEL_STOPPING_CALLBACK, this.onReelStoppingCallback.bind(this));

        this.on(FlowModuleEvents.BLOCKING_FLOW, this.onBlockFlowEnabled);
        this.on(FlowModuleEvents.NON_BLOCKING_FLOW, this.onBlockFlowDisabled);

        this.onView(ReelsViewEvents.SCATTER_HIT_ANIMATION_PLAYING, this.onScatterHitAnimationPlaying);

        this.on(FlowModuleEvents.FREESPINS_RESTORED, this.onFreespinsRestored);
        this.on(FlowModuleEvents.PICK_GAME_RESTORED, this.onPickGameRestored);

        this.on(ReelsEvents.ENABLE_SPIN_BY_SPIN_BUTTON, this.enableSpinBySpinButton);
        this.on(ReelsEvents.DISABLE_SPIN_BY_SPIN_BUTTON, this.disableSpinBySpinButton);

        this.getEventManager<GlobalEventsMapForBackwardCompatibility>().on('General.Slot.SPIN.DISABLE', this.disableSpin.bind(this), this);
        this.getEventManager<GlobalEventsMapForBackwardCompatibility>().on('General.Slot.SPIN.ENABLE', this.enableSpin.bind(this), this);
        this.getEventManager<GlobalEventsMap>().on('General.WinPresentation.PAYS_ANIMATION_STOPPED', this.onPaysAnimationStopped.bind(this), this);

        this.on(GenericPopupEvents.INTRO_STARTED, this.onPopupStarted);
        this.on(GenericPopupEvents.FADE_OUT_STARTED, this.onPopupFadeOutStarted);
        this.on(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed);
    }

    protected onPopupStarted(id: symbol, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.onFreeSpinsOutroStarted();
        }

        if (type === GenericPopupType.FREESPIN_RETRIGGER) {
            this.onFreeSpinsRetriggerStarted();
        }

        this.currentState.handle(id, type);
    }

    protected onPopupClosed(id: symbol, type: symbol): void {
        this.currentState.handle(id, type);
    }

    protected onPopupFadeOutStarted(id: symbol, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.onChangeReelsBackground(false);
        }

        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.onChangeReelsBackground(true);
        }

        this.currentState.handle(id, type);
    }

    protected setupViews(): void {
        this.setView(ReelsView.NAME);
    }

    protected setupStates(): void {
        this.addState(new ReelsBasicState(this, ReelsModule.BASIC_STATE));
        this.addState(new ReelsInitState(this, ReelsModule.INIT_STATE));
        this.addState(new ReelsSpinningState(this, ReelsModule.SPINNING_STATE));
        this.addState(new ReelsSequentialStopState(this, ReelsModule.STOPPING_STATE));
        this.addState(new ReelsQuickStopState(this, ReelsModule.QUICKSTOP_STATE));
    }

    public init(): void {
        super.init();

        this._reels = this.logic.getMainGameReelSet().getReels();
        this.gotoState(ReelsModule.INIT_STATE);
    }

    public setReelsSpinningTimes(): void {
        ReelConstants.REEL_BOUNCE_TIME = this.isTurboSpin() ? ReelBounceTime.turbo : ReelBounceTime.normal;
        ReelConstants.REEL_BOUNCE_EFFECT = this.isTurboSpin() ? Easing.Back.OutTurbo : Easing.Back.OutNormal;
        ReelConstants.FIRST_REEL_STOP_TIME = this.isTurboSpin() ? FirstReelStopTime.turbo : FirstReelStopTime.normal;
    }

    protected resetTurboSpinMode(): void {
        this._isTurboSpin = this.settings.get('quickSpin') && this.logic.nextAction !== Action.FREESPIN && this.logic.nextAction !== Action.RESPIN;
        this.view.toggleTurboSpin(this._isTurboSpin);
    }

    public isTurboSpin(): boolean {
        return this._isTurboSpin;
    }

    protected onSpinButtonPressed(): void {
        this.currentState.handle(SpinButtonEvents.SPIN_BUTTON_PRESSED);
    }

    protected onScatterHitAnimationPlaying(): void {
        this._scatterHitAnimationsCounter++;

        if (this.view.isQuickStop) {
            return;
        }

        this.soundPlayer.play('scatterhit' + this._scatterHitAnimationsCounter);
        this.view.scattersNumber--;
    }

    protected onInitialAnimationFinished(id: symbol): void {
        this.view.showAllSymbols();
        this.currentState.handle(id);
    }

    protected onWinPresentationPaused(): void {
        // this.view.showAllSymbols();
    }

    protected onPaysAnimationStopped(): void {
        this.view.showAllSymbols();
    }

    protected onFreeSpinsOutroStarted(): void {
        this.view.showAllSymbols();
    }

    protected onFreeSpinsRetriggerStarted(): void {
        this.view.showAllSymbols();
    }

    protected onChangeReelsBackground(isBaseGame: boolean): void {
        this.view.isBaseGame = isBaseGame;
        this.view.setReelsBackground();

        isBaseGame && this.restoreReelsetAfterFS();
    }

    protected restoreReelsetAfterFS(): void {
        const fgBonus = this.logic.getFreeGamesBonus();
        const isRubyCoinsGame = this.view.gameConfig.isRubyCoinsGame;

        if (isRubyCoinsGame && fgBonus && (fgBonus as any).isBuyBonus()) {
            this.gotoState(ReelsModule.INIT_STATE);

            return;
        }

        const spin: Spin = this.logic.getSessionForFreeSpinsWinningRound();

        this.view.setupReelStripWithPositions(this.logic.getReelsetByID(spin.reelSetId).getReels(), spin.reelPositions);
        isRubyCoinsGame && this.updateRubyCoinOnRestore(spin);
        this.view.reOrder();
        this.view.restoreOversizedSymbolsOnSpecialLayer();

        this._isRestoreAfterFS = true;
    }

    protected updateRubyCoinOnRestore(spinData: Spin): void {
        const symbolIds = spinData.symbolIds;

        symbolIds.forEach((reel, reelIndex) => {
            reel.forEach((symbol, rowIndex) => {
                if (this.view.gameConfig.reelsConfig.rubySymbol === symbol) {
                    this.view.updateRubyCoinOnRestoredReels(reelIndex, rowIndex);
                }
            });
        });
    }

    protected onAnticipationResultReceived(eventID: symbol, anticipatedReels: number[]): void {
        this._anticipatedReels = anticipatedReels;
    }

    protected onSpinResponseReceived(): void {
        this._isRestoreAfterFS = false;
        this._isReelsCanBeStopped = true;
        this.currentState.handle(LogicEvents.SPIN_RESPONSE);
    }

    protected onAllReelsStoppedCallback(): void {
        if (this.logic.getWin() > 0) {
            this.view.killEverythingFromAnimationsLayer();
            this.view.showAllSymbols();
        }
        this._isAllReelsStopped = true;
        this.eventManager.emit(ReelsEvents.ALL_REELS_STOPPED);

        this.currentState.handle(ReelsViewEvents.ALL_REELS_STOPPED);

        this._scatterHitAnimationsCounter = 0;
    }

    protected onAllReelAnimationsFinishedCallback(): void {
        this._isAllAnimationsFinished = true;
        this.currentState.handle(ReelsViewEvents.ALL_REEL_ANIMATIONS_FINISHED);
    }

    protected onReelStoppingCallback(eventID: string, reelIndex: number, distanceBeforeStop: number): void {
        this.eventManager.emit(ReelsEvents.REEL_START_STOPPING, reelIndex, distanceBeforeStop);
    }

    protected onPickGameRestored(_id: symbol, _isFreeSpins: symbol): void {
        const {spinData} = this.logic;

        this.restoreReels(this.logic.getReelsetByID(spinData.reelSetId), spinData.reelPositions);
    }

    protected restoreReels(reelSet: ReelSet, reelPositions: number[]): void {
        this.view.setupReelStripWithPositions(reelSet.getReels(), reelPositions);
        this.view.reOrder();
        this.view.restoreOversizedSymbolsOnSpecialLayer();
    }

    protected onFreespinsRestored(): void {
        this.view.isBaseGame = false;
        this.view.setReelsBackground();
        this.currentState.handle(FlowModuleEvents.FREESPINS_RESTORED);
    }

    protected onBlockFlowEnabled(): void {
        this._isBlockingFlow = true;
    }

    protected onBlockFlowDisabled(): void {
        this._isBlockingFlow = false;
    }

    protected onBetChanged(): void {
        this.view.showAllSymbols();
    }

    public resetParameters(): void {
        this._isReelsCanBeStopped = false;
        this._isAllReelsStopped = false;
        this._isAllAnimationsFinished = false;
        this.resetTurboSpinMode();
        this.setReelsSpinningTimes();
    }

    public removeReelFromAnticipatedList(reelIndex: number): void {
        this.anticipatedReels.splice(this.anticipatedReels.indexOf(reelIndex), 1);
    }

    protected enableSpinBySpinButton(): void {
        this.enableSpin();
    }

    protected disableSpinBySpinButton(): void {
        this.disableSpin();
    }

    protected enableSpin(reason: ReasonsForDisablingSpin = 'default'): void {
        const index = this._spinDisabledBy.indexOf(reason);

        if (index !== -1) {
            this._spinDisabledBy.splice(index, 1);
        }

        this._canSpinBySpinButton = this._spinDisabledBy.length === 0;
    }

    protected disableSpin(reason: ReasonsForDisablingSpin = 'default'): void {
        if (this._spinDisabledBy.indexOf(reason) === -1) {
            this._spinDisabledBy.push(reason);
        }

        this._canSpinBySpinButton = this._spinDisabledBy.length === 0;
    }

    public get reels(): Reel[] {
        return this._reels;
    }

    public get isAllAnimationsFinished(): boolean {
        return this._isAllAnimationsFinished;
    }

    public get isAllReelsStopped(): boolean {
        return this._isAllReelsStopped;
    }

    public get isReelsCanBeStopped(): boolean {
        return this._isReelsCanBeStopped;
    }

    public set isReelsCanBeStopped(value: boolean) {
        this._isReelsCanBeStopped = value;
    }

    public set isRestoreAfterFS(value: boolean) {
        this._isRestoreAfterFS = value;
    }

    public get anticipatedReels(): number[] {
        return this._anticipatedReels;
    }

    public get isBlockingFlow(): boolean {
        return this._isBlockingFlow;
    }

    public get canSpinBySpinButton(): boolean {
        return this._canSpinBySpinButton;
    }
}
